import find from 'lodash/find';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import ConditionRow from './ConditionRow';
import { Excludes, RoutingGroupCondition as RoutingGroupConditionType } from 'src/actions/rules/condition';
import { StyledDropdown } from 'src/presentation/components/pages/rules/shared/StyledComponents';
import useRoutingGroups from '../hooks/useRoutingGroups';

type RoutingGroupConditionProps = {
  condition: RoutingGroupConditionType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const RoutingGroupCondition: FC<RoutingGroupConditionProps> = ({
  condition,
  index,
  isReadOnly,
  onDelete,
  onUpdate,
}) => {
  const onExclusionChange = useCallback(
    val => {
      condition.setNot(val === Excludes.True);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onRoutingGroupChange = useCallback(
    routingGroupId => {
      condition.updateRoutingGroup(routingGroupId);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const { loadingState, values: routingGroupOptions } = useRoutingGroups();
  if (loadingState === 'loading') return null;

  return (
    <ConditionRow index={index} isReadOnly={isReadOnly} onDelete={onDelete} text="Inbox">
      {isReadOnly ? renderReadOnlyCondition() : renderEditableCondition()}
    </ConditionRow>
  );

  function renderEditableCondition() {
    return (
      <>
        <StyledExclusionDropdown
          data-aid="routingGroupConditionExclusionDropdown"
          onSelect={onExclusionChange}
          options={exclusionOptions}
          value={condition.not ? Excludes.True : Excludes.False}
        />
        <StyledDropdown
          data-aid="routingGroupConditionValuesDropdown"
          isMultiSelect
          leftMargin
          maxRows={5}
          multiSelectLabel="inbox"
          onSelect={onRoutingGroupChange}
          options={routingGroupOptions}
          placeholder="Select inbox"
          searchable
          value={condition.value}
        />
      </>
    );
  }

  function renderReadOnlyCondition() {
    return (
      <>
        <StyledExclusionLabel data-aid="readOnlyExclusionValue">
          {renderExclusionLabel(condition.not ? Excludes.True : Excludes.False)}
        </StyledExclusionLabel>
        <span data-aid="readOnlyRoutingGroupValues">
          {renderRoutingGroupNames(condition.value, routingGroupOptions)}
        </span>
      </>
    );
  }

  function renderExclusionLabel(value: string) {
    const exclusion = find(exclusionOptions, { value });
    return exclusion ? exclusion.label : '';
  }

  function renderRoutingGroupNames(routingGroups, routingGroupOptions) {
    return routingGroups.map(routingGroup => find(routingGroupOptions, { value: routingGroup }).label).join(', ');
  }
};

const StyledExclusionDropdown = styled(StyledDropdown)`
  width: 115px;
`;

const StyledExclusionLabel = styled.span`
  margin-right: 8px;
`;

// dropdown selection does not render when using false as a boolean
const exclusionOptions: { label: string; value: string }[] = [
  { label: 'Includes', value: Excludes.False },
  { label: 'Excludes', value: Excludes.True },
];

export default RoutingGroupCondition;
